import React from 'react';
import { useForm, Controller } from 'react-hook-form';
import { useMutation } from 'react-query';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Typography,
  FormControlLabel,
  Checkbox,
  Button,
  Paper,
  Divider,
  Alert,
} from '@mui/material';
import SwalReact from 'sweetalert2-react-content';
import Swal from 'sweetalert2';
import { updateGeneralSettings } from 'services/transparency-config-service';
import { DotsLoader } from 'Components/DotsLoader';

const MySwal = SwalReact(Swal);

export const GeneralSettings = ({ config, onUpdate, companyKey }) => {
  const { t } = useTranslation(['transparency', 'common']);
  const { control, handleSubmit, watch } = useForm({
    defaultValues: {
      transparencyEnabled: config.transparencyEnabled || false,
      publicAccessEnabled: config.publicAccessEnabled || false,
      protectedAccessEnabled: config.protectedAccessEnabled || false,
      showTrendPublicly: config.showTrendPublicly || false,
    },
  });

  const transparencyEnabled = watch('transparencyEnabled');

  const mutation = useMutation(
    (data) => updateGeneralSettings(companyKey, data),
    {
      onSuccess: () => {
        MySwal.fire({
          title: t('transparency:generalSettings.notifications.success'),
          text: t('transparency:generalSettings.notifications.successText'),
          icon: 'success',
        });
        onUpdate();
      },
      onError: (error) => {
        MySwal.fire({
          title: t('transparency:generalSettings.notifications.error'),
          text: error.response?.data?.message || error.message,
          icon: 'error',
        });
      },
    }
  );

  const onSubmit = (data) => {
    mutation.mutate(data);
  };

  return (
    <Paper sx={{ p: 3 }}>
      <form onSubmit={handleSubmit(onSubmit)}>
        <Typography variant="h6" gutterBottom>
          {t('transparency:generalSettings.title')}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          {t('transparency:generalSettings.subtitle')}
        </Typography>

        <Divider sx={{ my: 2 }} />

        {/* Master Toggle */}
        <Box sx={{ mb: 3 }}>
          <Controller
            control={control}
            name="transparencyEnabled"
            render={({ field: { onChange, value } }) => (
              <FormControlLabel
                control={
                  <Checkbox
                    checked={value}
                    onChange={onChange}
                    color="primary"
                  />
                }
                label={
                  <Box>
                    <Typography fontWeight={500}>
                      {t('transparency:generalSettings.enableTransparency.label')}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {t('transparency:generalSettings.enableTransparency.caption')}
                    </Typography>
                  </Box>
                }
              />
            )}
          />
        </Box>

        {!transparencyEnabled && (
          <Alert severity="warning" sx={{ mb: 3 }}>
            {t('transparency:generalSettings.disabledWarning')}
          </Alert>
        )}

        {/* Access Mode Toggles */}
        <Box sx={{ ml: 4, mb: 3, opacity: transparencyEnabled ? 1 : 0.5 }}>
          <Controller
            control={control}
            name="publicAccessEnabled"
            render={({ field: { onChange, value } }) => (
              <FormControlLabel
                disabled={!transparencyEnabled}
                control={
                  <Checkbox
                    checked={value}
                    onChange={onChange}
                    color="primary"
                  />
                }
                label={
                  <Box>
                    <Typography>{t('transparency:generalSettings.enablePublic.label')}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {t('transparency:generalSettings.enablePublic.caption')}
                    </Typography>
                  </Box>
                }
              />
            )}
          />
        </Box>

        <Box sx={{ ml: 4, mb: 3, opacity: transparencyEnabled ? 1 : 0.5 }}>
          <Controller
            control={control}
            name="protectedAccessEnabled"
            render={({ field: { onChange, value } }) => (
              <FormControlLabel
                disabled={!transparencyEnabled}
                control={
                  <Checkbox
                    checked={value}
                    onChange={onChange}
                    color="primary"
                  />
                }
                label={
                  <Box>
                    <Typography>{t('transparency:generalSettings.enableProtected.label')}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {t('transparency:generalSettings.enableProtected.caption')}
                    </Typography>
                  </Box>
                }
              />
            )}
          />
        </Box>

        <Divider sx={{ my: 2 }} />

        {/* Trend Toggle */}
        <Box sx={{ mb: 3, opacity: transparencyEnabled ? 1 : 0.5 }}>
          <Controller
            control={control}
            name="showTrendPublicly"
            render={({ field: { onChange, value } }) => (
              <FormControlLabel
                disabled={!transparencyEnabled}
                control={
                  <Checkbox
                    checked={value}
                    onChange={onChange}
                    color="primary"
                  />
                }
                label={
                  <Box>
                    <Typography>{t('transparency:generalSettings.showTrend.label')}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {t('transparency:generalSettings.showTrend.caption')}
                    </Typography>
                  </Box>
                }
              />
            )}
          />
        </Box>

        <Divider sx={{ my: 2 }} />

        {/* Submit */}
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
          <Button
            type="submit"
            variant="contained"
            disabled={mutation.isLoading}
          >
            {mutation.isLoading ? <DotsLoader /> : t('transparency:generalSettings.actions.save')}
          </Button>
        </Box>
      </form>
    </Paper>
  );
};
